// Single source of truth for QVolt content (scraped at build time).
import data from "../data/qvolt_content.json";

export const content = data;

const DG = {
  hero: "/images/qvolt/hero.jpeg",
  team: "/images/qvolt/team.webp",
  panels: "/images/qvolt/s2.webp",
  batteries: "/images/qvolt/s5.webp",
  // Card-sized (max 800px) renditions of the install photos. The full-size
  // files (max 1600px) still exist alongside for the About / location
  // galleries; the home-page cards render at ~400 CSS px so the 800px
  // variants are already 2x.
  installation:
    "/images/qvolt/87652004_798891637302598_2468445905490018304_n-scaled-1-800.webp",
  gallery1:
    "/images/qvolt/72581002_2312284025768847_6494481358939750400_n-scaled-1-800.webp",
  gallery2:
    "/images/qvolt/74264723_879922539072025_4236768126907187200_n-scaled-1-800.webp",
  gallery3:
    "/images/qvolt/87580406_248888032789088_5592611614885937152_n-scaled-1-800.webp",
};

/** Poster frame for the hero promo video (portrait 3:4, 600×800 WebP). */
export const PROMO_VIDEO_POSTER = "/images/qvolt/promo-poster.webp";
export const PROMO_VIDEO_POSTER_SRCSET =
  "/images/qvolt/promo-poster-300.webp 300w, /images/qvolt/promo-poster-450.webp 450w, /images/qvolt/promo-poster.webp 600w";

/**
 * srcset for the local install photos used on cards. Each `…-800.webp` has a
 * `…-400.webp` sibling (generated alongside); remote / other images get no
 * srcset. */
export const cardSrcSet = (src) =>
  typeof src === "string" && /-800\.webp$/.test(src)
    ? `${src.replace(/-800\.webp$/, "-400.webp")} 400w, ${src} 800w`
    : undefined;

function pickHero() {
  // Phase 24 polish — the image list was rehosted to local /images/qvolt/*
  // paths, and the old "contains qvoltsolar.com.au" match was catching a
  // Facebook pixel URL (its event_url param contains the domain), which
  // rendered as a broken founder photo on /about. Only accept local image
  // files now; fall back to the design-guideline hero.
  const all = data.all_image_urls || [];
  const candidate = all.find(
    (u) =>
      typeof u === "string" &&
      u.startsWith("/images/qvolt/") &&
      /\.(jpe?g|png|webp|avif)$/i.test(u) &&
      /(hero|rooftop|install|dan)/i.test(u)
  );
  return candidate || DG.hero;
}

/* -------------------------------------------------------------------------
   HERO — verbatim from live qvoltsolar.com.au home H2 ("Powering Homes in
   Queensland with Solar Power"). Subhead is verbatim from the live About
   intro on the same site.
   ------------------------------------------------------------------------- */
export const HERO_EYEBROW = "Solar panel systems and batteries"; // live H2 eyebrow

export const HERO_HEADLINE = "Powering homes in Queensland with solar power.";

export const HERO_SUBHEAD =
  "Hey there, my name is Daniel Quinn (call me Dan) and I have been living and breathing solar for over 10 years now.";

/* -------------------------------------------------------------------------
   WHY QVOLT — the live site's "5 Reasons to Choose My Company!" H3 titles
   (verbatim) plus body copy paraphrased from the same page's surrounding
   paragraphs. Titles are 100% verbatim; bodies are pulled from the live
   marketing copy on the home page (verbatim sentences).
   ------------------------------------------------------------------------- */
export const VALUE_PROPS = [
  {
    icon: "UserCheck",
    title: "Deal with a solar expert, not a salesperson.",
    body:
      "For honest, straight to the point and unbiased (commission free) advice, look no further than QVolt!",
  },
  {
    icon: "Settings2",
    title: "We don't do set packages, we tailor solutions perfectly to your needs.",
    body:
      "Your home is different to everyone else's and your solar needs are different which is why we tailor a package to your needs rather than box you in to set packages.",
  },
  {
    icon: "Award",
    title: "Experienced, knowledgeable and the highest quality.",
    body:
      "10+ years in solar means I know a lemon from a rocket. With QVolt you will only get sound advice that has helped 1000's of homeowners get the perfect system.",
  },
  {
    icon: "ShieldCheck",
    title: "All installations backed by 30 year workmanship warranty.",
    body:
      "Choose a solar provider with the peace of mind knowing that you will be looked after for a decade and then some.",
  },
  {
    icon: "Home",
    title: "Small, family owned business that keeps pricing low!",
    body:
      "We are not the type to love you and leave you. We stick around for the long term and provide you and your family with support and maintenance.",
  },
];

/* -------------------------------------------------------------------------
   SERVICES — verbatim H3 titles from the live home page services strip.
   ------------------------------------------------------------------------- */
export const SERVICES = [
  {
    title: "Advice and Guidance",
    desc:
      "Honest, straight to the point and unbiased (commission free) solar advice tailored to your home.",
    image: DG.panels,
    href: "/services",
  },
  {
    title: "Sales and Packages",
    desc:
      "From tailoring the perfect packages for every home to product selection. Every system built around your roof.",
    image: DG.batteries,
    href: "/services/solar-panel-sales-and-packages",
  },
  {
    title: "Installation and Configuration",
    desc:
      "Expert installation and configuration. Once we narrow down the best system for you, my team and I install and configure it for maximum performance.",
    image: DG.installation,
    href: "/services/solar-panel-installation",
  },
];

/* -------------------------------------------------------------------------
   WARRANTIES — 4 cards: 30 workmanship / 30 panels / 10 inverter / 10 battery.
   Backed by /api/content.warranties (workmanship_years, panels_years,
   inverters_years, battery_years).
   ------------------------------------------------------------------------- */
export const WARRANTIES = [
  { years: 30, label: "Workmanship warranty", icon: "Shield", verbatim: "30 year workmanship warranty" },
  { years: 30, label: "Panel warranty", icon: "SunMedium", verbatim: "30 year panel warranty" },
  { years: 10, label: "Inverter warranty", icon: "Cpu", verbatim: "10 year inverter warranty" },
  { years: 10, label: "Battery warranty", icon: "BatteryCharging", verbatim: "10 year battery warranty" },
];

/* -------------------------------------------------------------------------
   No-cost promises retained for the contact + careers areas.
   ------------------------------------------------------------------------- */
export const NO_COST_PROMISES = ["No deposit", "No out of pocket expense", "No upfront payment"];

/* -------------------------------------------------------------------------
   RATING — verbatim ("Rated 5/5 From 200+ Google Reviews").
   ------------------------------------------------------------------------- */
export const RATING = {
  value: "5",
  outOf: "5",
  source: "200+ Google reviews",
};

/* -------------------------------------------------------------------------
   BLOG — only qvoltsolar.com.au domain images used on the home.
   ------------------------------------------------------------------------- */
export const BLOG_TEASERS = (() => {
  const rotation = [DG.gallery1, DG.gallery2, DG.gallery3];
  const fallback = [
    {
      slug: "looking-for-solar-installers-in-brisbane",
      title:
        "Looking for solar installers in Brisbane? 10 things to know before you buy",
      hero_image_url: rotation[0],
    },
    {
      slug: "the-1-may-2026-battery-rebate-changes",
      title: "Why the 1 May 2026 battery rebate changes matter",
      hero_image_url: rotation[1],
    },
    {
      slug: "7-ways-solar-panels-can-slash-your-queensland-power-bills",
      title: "7 ways solar can slash your Queensland power bills",
      hero_image_url: rotation[2],
    },
  ];
  const posts = (data.blog_posts || []).filter((p) => p && p.title).slice(0, 3);
  if (posts.length < 3) return fallback;
  return posts.map((p, i) => ({
    slug: p.slug,
    title: p.title,
    hero_image_url:
      p.hero_image_url && p.hero_image_url.includes("qvoltsolar.com.au")
        ? p.hero_image_url
        : rotation[i % rotation.length],
  }));
})();

/* -------------------------------------------------------------------------
   NAV / CONTACT / LICENCES
   ------------------------------------------------------------------------- */
export const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Calculator", href: "/solar-calculator" },
  { label: "Learning Centre", href: "/learning-centre" },
  { label: "Contact", href: "/contact" },
];

export const CONTACT = {
  ...(data.contact || {}),
  address:
    (data.contact && data.contact.address) ||
    "53/28 Burnside Rd, Ormeau QLD 4208",
  email: (data.contact && data.contact.email) || "support@qvoltsolar.com",
  phone: (data.contact && data.contact.phone) || "1300 960 073",
  // Iteration 4: align service areas with user-confirmed list.
  service_areas: ["Brisbane", "Gold Coast", "Sunshine Coast", "Toowoomba"],
};

export const LICENCES = data.licences || { QLD: "#88205", NSW: "466291c" };

/* CTA band — "Get in Touch Today!" matches the live-site H2 verbatim. */
export const CTA_BAND = {
  eyebrow: "Ready when you are",
  headline: "Get in Touch Today!",
  body:
    "Get a no-obligation quote from a real solar expert. Honest advice, no commission-only sales reps, no pushy follow-ups.",
  primary: "Get a free quote",
  // Phase 24 polish — the secondary button dials the office, so label it
  // as a call (it used to say "How it works" while opening tel:).
  secondary: "Call 1300 960 073",
};

export const HERO_IMAGE = pickHero();

export const IMAGES = DG;

export const QVOLT_LOGO = "/qvolt-logo.png"; // transparent PNG, no black bg
export const NETCC_BADGE = "/netcc-badge.webp"; // 650×426
// Small rendition for the 24–48px-tall trust pills / footer badge.
export const NETCC_BADGE_SM = "/netcc-badge-144.webp"; // 220×144
export const NETCC_URL = "https://www.newenergytech.org.au/";
export const TEAM_PHOTO = "/team.webp";
export const GOOGLE_G_LOGO = "/google-g.svg";

/* Routes used by Header + Footer. */
export const ROUTES = {
  home: "/",
  about: "/about",
  services: "/services",
  calculator: "/solar-calculator",
  contact: "/contact",
  blog: "/blog",
};

export const GOOGLE_REVIEWS = {
  rating: 4.7,
  count: 355,
  // Fallback link to Google Maps search for the office (no API key needed).
  url:
    "https://www.google.com/maps/search/?api=1&query=QVolt+Solar+Ormeau+QLD",
  star_color: "#FBBC04",
};

export const MAP_EMBED_SRC =
  "https://www.google.com/maps?q=53%2F28+Burnside+Rd%2C+Ormeau+QLD+4208&output=embed";

export const TEAM_BAND = {
  eyebrow: "Meet the team",
  // Sentence below is composed; the rest of this band reuses the live-site
  // copy verbatim (see `body_verbatim` field).
  headline: "Real installers. Real Queenslanders.",
  body_verbatim:
    "We're a small family-owned crew. From tailoring the perfect packages for every home to product selection, advice, installation and everything in between, let my knowledge and love of solar help you get the right solution the first time and forever.",
  // Iteration 4: licence pills only.
  stats: [
    { icon: "BadgeCheck", label: "QLD electrical #88205" },
    { icon: "BadgeCheck", label: "NSW 466291c" },
  ],
};
